import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as yup from "yup";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import ExternalLink from "../../../../components/ExternalLink";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import insight_vm_4 from "./images/insight_vm_4.png";
import insight_vm_5 from "./images/insight_vm_5.png";
import insight_vm_org_settings from "./images/insight_vm_org_settings.png";
import insights_vm_api_key from "./images/insights_vm_api_key.png";

const Rapid7InsightVMIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const [showSecret, setShowSecret] = useState(false);
  const { isIntegrated, isIntegratedLoading } =
    useIsIntegrated("rapid7_insight_vm");
  const [loading, setLoading] = useState(false);
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const handleClickShowSecret = () => setShowSecret(!showSecret);
  const handleMouseDownSecret = () => setShowSecret(!showSecret);
  const colors = useColors();
  const regionOptions = [
    { value: "us", label: "United States - 1 (us)" },
    { value: "us2", label: "United States - 2 (us2)" },
    { value: "us3", label: "United States - 3 (us3)" },
    { value: "eu", label: "Europe (eu)" },
    { value: "au", label: "Australia (au)" },
    { value: "ca", label: "Canada (ca)" },
    { value: "jp", label: "Japan (jp)" },
  ];

  const formInitialValues = {
    api_key: "",
    region_code: "",
    organization_id: "",
  };

  const formCheckoutSchema = yup.object().shape({
    api_key: yup.string().required("required"),
    region_code: yup.string().required("required"),
    organization_id: yup.string().required("required"),
  });

  const { registerRapid7InsightVMIntegration } = useAPI();

  const handleFormSubmit = (values) => {
    setLoading(true);
    registerRapid7InsightVMIntegration(
      values.api_key.trim(),
      values.region_code,
      values.organization_id.trim()
    )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Rapid7 Insight VM integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from Rapid7 and prioritize the suggested remediation."
            : ""
        }
        image={logos.Rapid7Logo}
        iconBorderRadius={0}
        name={"Rapid7 InsightVM"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>
                Rapid7 Insight VM is integrated!
              </Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <Box>
                <Formik
                  onSubmit={handleFormSubmit}
                  initialValues={formInitialValues}
                  validationSchema={formCheckoutSchema}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                  }) => (
                    <Form>
                      <GuidelineStepByStep>
                        <GuidelineStep title="Fill in the Organization details">
                          <ExternalLink
                            href="https://insight.rapid7.com/login"
                            text="1. Log in as Platform Admin to"
                            linkText={"Rapid7"}
                          />
                          <ExternalLink
                            href="https://insight.rapid7.com/platform#/administration/settings/organizationSettings"
                            text="2. Go to"
                            linkText={"Rapid7 Organization Settings"}
                          />
                          <Typography variant={"body2"}>
                            3. Select the appropriate{" "}
                            <strong>region code</strong>
                          </Typography>
                          <FormControl
                            variant="outlined"
                            style={{ margin: "16px 0", minWidth: 200 }}
                          >
                            <InputLabel>Region Code</InputLabel>
                            <Select
                              label="Region Code"
                              name="region_code"
                              value={values.region_code}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                !!touched.region_code && !!errors.region_code
                              }
                              disabled={guideMode}
                            >
                              {regionOptions.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {touched.region_code && errors.region_code && (
                              <FormHelperText sx={{ color: colors.error_main }}>
                                {errors.region_code}
                              </FormHelperText>
                            )}
                          </FormControl>
                          <TextField
                            fullWidth
                            variant="filled"
                            name="organization_id"
                            label="Organization ID"
                            value={values.organization_id}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            autoComplete="off"
                            error={
                              !!touched.organization_id &&
                              !!errors.organization_id
                            }
                            helperText={
                              touched.organization_id && errors.organization_id
                            }
                            style={{ width: "700px" }}
                            disabled={guideMode}
                          />
                          <ScreenshotAccordion
                            images={[
                              {
                                file: insight_vm_org_settings,
                                altext: "Organization Settings",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                        </GuidelineStep>

                        <GuidelineStep title="Create a new Organization API Key">
                          <ExternalLink
                            href={
                              "https://insight.rapid7.com/platform#/administration/apiKeyManagement/organization"
                            }
                            text="1. Go to"
                            linkText={"API Key Management"}
                          />

                          <Typography variant={"body2"}>
                            2. Click{" "}
                            <strong>Generate New Organization Key</strong>
                          </Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: insights_vm_api_key,
                                altext: "Generate New Organization Key",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                          <Typography variant={"body2"}>
                            3. Select an organization and provide a name for the
                            key, e.g, <strong>Breeze Integration</strong>
                            .
                            <CustomCopyButton
                              copyText="Breeze Integration"
                              copyTitle={""}
                            />
                          </Typography>
                          <Typography variant={"body2"}>
                            4. Click <strong>Submit</strong>
                          </Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: insight_vm_4,
                                altext: "Submit",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                        </GuidelineStep>
                        <GuidelineStep title="Copy Your API Key">
                          <Typography variant={"body2"}>
                            A new window opens and displays the generated key
                          </Typography>
                          <Typography variant={"body2"}>
                            <strong>Copy the key</strong>. You will not be able
                            to view it again after you close the window.
                          </Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: insight_vm_5,
                                altext: "Copy the key",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                          <Typography variant={"body2"}>
                            Paste the key below.
                          </Typography>

                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                          >
                            <TextField
                              fullWidth
                              variant="filled"
                              name="api_key"
                              label="API Key"
                              value={values.api_key}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              autoComplete="off"
                              type={showSecret ? "text" : "password"}
                              error={!!touched.api_key && !!errors.api_key}
                              helperText={touched.api_key && errors.api_key}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label="toggle password visibility"
                                      onClick={handleClickShowSecret}
                                      onMouseDown={handleMouseDownSecret}
                                    >
                                      {showSecret ? (
                                        <Visibility />
                                      ) : (
                                        <VisibilityOff />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                              style={{ width: "700px" }}
                              disabled={guideMode}
                            />
                          </Box>
                          <PrimaryButton
                            sx={{ width: "100px" }}
                            type="submit"
                            disabled={guideMode}
                          >
                            Connect
                          </PrimaryButton>
                        </GuidelineStep>
                      </GuidelineStepByStep>
                    </Form>
                  )}
                </Formik>
              </Box>
            </div>
          )
        }
      />
    </Box>
  );
};

export default Rapid7InsightVMIntegration;
